import * as moment from 'moment/moment';
import { componentNameMap } from './commonConstants';

/**
 * Router navigation descriptor produced by a registry entry for a search result.
 * Consumers call `router.navigate(commands, { queryParams })`.
 */
export interface SearchResultNavigation {
  commands: any[];
  queryParams?: any;
}

/**
 * Config-driven registry describing every entity type surfaced by GET /v1/search.
 *
 * - `apiKey` is the key in the /v1/search response payload. All keys are treated
 *   as optional (api master only returns dailyReports/issues).
 * - `searchType` is stamped on each result and used as the byType map key.
 * - `tabSlug` MUST equal label.toLowerCase().replaceAll(' ', '-') because the
 *   search-details tab handler derives the `tab` query param from the tab label.
 * - `card` picks the renderer on the /search page: the two legacy bespoke cards
 *   or the generic card (title/snippet/meta/status from this config).
 * - Either `panelComponent` (open right panel on /search) or `navigationFor`
 *   (leave /search) handles clicks.
 *
 * NOTE: mgvwRecords is intentionally NOT registered — the web app has no MGVW
 * UI (the MGVW agency view is mobile-only), so there is nowhere to send the
 * user. The extra key in the API response is simply ignored.
 */
export interface SearchEntityConfig {
  apiKey: string;
  searchType: string;
  label: string;
  /** Singular form of the label, shown as the per-row category in the header typeahead. */
  singularLabel: string;
  tabSlug: string;
  icon: string;
  /** Field holding the ISO timestamp used to stamp `createdAt`. */
  createdAtField: string;
  /** Field the /search date-range filter compares against. */
  dateField: string;
  /** Tab is rendered even when there are zero results (legacy behavior). */
  alwaysShowTab?: boolean;
  card: 'dailyReport' | 'issue' | 'generic';
  /** Per-result icon override (falls back to `icon`), e.g. by matched component. */
  iconOf?: (result: any) => string;
  titleOf?: (result: any) => string;
  snippetOf?: (result: any) => string;
  metaOf?: (result: any) => string;
  statusOf?: (result: any) => string;
  /** Extra "N matches (...)" line in the header typeahead (daily reports only). */
  matchInfoOf?: (result: any) => string;
  navigationFor?: (result: any) => SearchResultNavigation | null;
  /** When set, clicking opens the /search right panel with this component. */
  panelComponent?: any;
}

const fullName = (user: any): string =>
  [user?.first_name, user?.last_name].filter(Boolean).join(' ');

/**
 * Joins meta segments with the ' . ' convention, dropping empties and
 * duplicates (e.g. sites named identically to their project).
 */
const joinMeta = (parts: any[]): string => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of parts) {
    const text = typeof part === 'string' ? part.trim() : '';
    // Normalized dedupe (case + punctuation insensitive): project vs site
    // names are often near-duplicates in real data ("...Toll Road, Segments
    // B-3" / "...toll road segments B-3") and showing both is pure noise.
    const key = text.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (text && !seen.has(key)) {
      seen.add(key);
      out.push(text);
    }
  }
  return out.join(' . ');
};

/**
 * `_matched_fields` labels that describe denormalized metadata rather than the
 * record's own content (e.g. a project-number query legitimately matches every
 * record of that project via "project number"). Only used as a conservative
 * fallback when the query text isn't available on the result.
 */
const METADATA_FIELD_PREFIXES = ['project ', 'organization ', 'entity ', 'site ', 'punchlist '];

/**
 * Tokenizes a search query mirroring the server semantics: case-insensitive,
 * split on non-alphanumerics, drop tokens shorter than 3 characters.
 */
// The index only holds edge-ngrams up to this many characters, so the api
// truncates each query token to it before matching (search-service.js).
// Highlighting the untruncated token would leave a genuine hit unhighlighted:
// "subcontractor" is matched server-side as "subcontrac", so a record reading
// "subcontracting" comes back with nothing highlighted.
const MAX_NGRAM = 10;

export const tokenizeQuery = (searchText: any): string[] =>
  (typeof searchText === 'string' ? searchText : '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(token => token.length >= 3)
    .map(token => token.slice(0, MAX_NGRAM));

/**
 * True when any query token prefix-matches a word of the given text
 * (case-insensitive), mirroring the server's match semantics.
 */
export const textMatchesQuery = (text: any, tokens: string[]): boolean => {
  if (typeof text !== 'string' || !text || !tokens?.length) {
    return false;
  }

  const words = text.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

  return tokens.some(token => words.some(word => word.startsWith(token)));
};

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  '\'': '&#39;',
};

const escapeHtml = (text: string): string =>
  text.replace(/[&<>"']/g, c => HTML_ESCAPES[c]);

/**
 * Returns HTML-escaped text with query-token prefix matches wrapped in the
 * app's global `.highlighted-value` band (#BDC9FF). Safe for [innerHTML].
 */
export const highlightQueryTokens = (text: any, searchText: any): string => {
  const safeText = typeof text === 'string' ? text : '';
  const tokens = tokenizeQuery(searchText);

  if (!safeText || !tokens.length) {
    return escapeHtml(safeText);
  }

  // Split retaining word/separator parts so the original text is preserved.
  return safeText.split(/([a-zA-Z0-9]+)/).map(part => {
    const matchedToken = /^[a-zA-Z0-9]+$/.test(part)
      ? tokens.find(token => part.toLowerCase().startsWith(token))
      : null;

    if (matchedToken) {
      const prefix = part.slice(0, matchedToken.length);
      const rest = part.slice(matchedToken.length);
      return `<span class="highlighted-value">${escapeHtml(prefix)}</span>${escapeHtml(rest)}`;
    }

    return escapeHtml(part);
  }).join('');
};

/**
 * Maps a daily-report `_matched_excerpts` field label (by longest matching
 * prefix) to the block name web users know from the daily-report page.
 */
const DAILY_REPORT_BLOCK_LABELS: Array<[string, string]> = [
  ['bid items', 'Bid items'],
  ['material deliveries', 'Deliveries'],
  ['labor', 'Labor & equipment'],
  ['equipment', 'Labor & equipment'],
  // The api relabels a control-form excerpt to the widget that owns the
  // matched form (search-service FORM_WIDGET_EXCERPT_PREFIX); longest prefix
  // wins below, so 'control forms' keeps meaning the plain "Forms" block.
  ['control point forms', 'Control point forms'],
  ['testing forms', 'Testing forms'],
  ['control forms', 'Forms'],
  ['internal tests', 'Material tests'],
  ['external tests', 'Material tests'],
  ['activity codes', 'Activity codes'],
  ['activities', 'Activities'],
  ['issues', 'Issues'],
  ['weather', 'Weather'],
  ['created by', 'Created by'],
  ['createdby', 'Created by'],
];

const capitalize = (text: string): string =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

const dailyReportBlockLabel = (field: string): string => {
  const lower = (field || '').toLowerCase();
  const match = DAILY_REPORT_BLOCK_LABELS
    .filter(([prefix]) => lower.startsWith(prefix))
    .sort((a, b) => b[0].length - a[0].length)[0];

  return match ? match[1] : capitalize(field);
};

const firstValidExcerpt = (result: any): { field: string; text: string } | null => {
  const excerpts = Array.isArray(result?._matched_excerpts) ? result._matched_excerpts : [];

  return excerpts.find(e => typeof e?.field === 'string' && e.field
    && typeof e?.text === 'string' && e.text) || null;
};

/**
 * Builds a safe-HTML hint for results where the rendered card text (title +
 * snippet) contains NO query-token match — e.g. a query hitting the report
 * author or the lab name — so the card shows why it's in the results:
 *
 * - With `_matched_excerpts` (newer api): "<Field label>: <excerpt>" with the
 *   query tokens banded via .highlighted-value. Daily-report excerpts map the
 *   field to the block name users know (e.g. "Labor & equipment: ...") and
 *   render under the existing "N matches" summary line.
 * - Without excerpts: plain "Matched: <label>, <label>" fallback.
 * - Null when the card already shows the keyword or nothing matched (old api).
 *
 * Relies on the effect stamping `searchQuery` on each result; without it,
 * falls back to the conservative metadata-only rule for the label path.
 */
export const matchedHintHtmlOf = (result: any): string | null => {
  const matchedFields = Array.isArray(result?._matched_fields) ? result._matched_fields : [];
  const excerpt = firstValidExcerpt(result);

  if (!matchedFields.length && !excerpt) {
    return null;
  }

  const config = searchEntityBySearchType[result?.searchType];

  // Daily reports: the "N matches (components)" summary already explains the
  // match — only add the block-labeled excerpt line when we have an excerpt.
  if (config?.matchInfoOf) {
    if (!excerpt) {
      return null;
    }

    return `${escapeHtml(dailyReportBlockLabel(excerpt.field))}: ${highlightQueryTokens(excerpt.text, result?.searchQuery)}`;
  }

  const tokens = tokenizeQuery(result?.searchQuery);

  if (tokens.length) {
    const renderedText = `${config?.titleOf?.(result) || ''} ${config?.snippetOf?.(result) || ''}`;

    if (textMatchesQuery(renderedText, tokens)) {
      return null;
    }
  } else {
    // No query context on the result: only hint for metadata-only matches.
    const isMetadataLabel = (label: any) => typeof label === 'string'
      && METADATA_FIELD_PREFIXES.some(prefix => label.startsWith(prefix));

    if (!matchedFields.length || !matchedFields.every(isMetadataLabel)) {
      return null;
    }
  }

  if (excerpt) {
    return `${escapeHtml(capitalize(excerpt.field))}: ${highlightQueryTokens(excerpt.text, result?.searchQuery)}`;
  }

  const uniqueLabels = Array.from(new Set(matchedFields.filter(label => typeof label === 'string' && label)));

  if (!uniqueLabels.length) {
    return null;
  }

  return escapeHtml(`Matched: ${uniqueLabels.slice(0, 2).join(', ')}`);
};

/**
 * Readable labels for off-site material test statuses / lab recommendations,
 * matching the wording of the /offsite-tests page filters.
 */
const MATERIAL_TEST_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  sent_to_lab: 'Request sent to lab',
  lab_scheduled: 'Lab visit scheduled',
  lab_activity_completed: 'Lab visit recorded',
  updated_report: 'Lab uploaded report',
  passed: 'Pass',
  failed: 'Fail',
  cancelled: 'Cancelled',
  n_a: 'N/A',
};

/**
 * `control_forms` is one server component but three report widgets — each
 * form belongs to the widget named by its template's display_widget (api
 * stamps it on every matched form in component_details). Name the widgets
 * that actually matched instead of the umbrella component.
 */
const FORM_WIDGET_COMPONENT_NAMES: Record<string, string> = {
  forms: 'forms',
  control_forms: 'control point forms',
  testing_forms: 'testing forms',
};

/**
 * Daily-report page-menu icon per matched component, so a hit on bid items
 * shows the bid-item icon, a labor hit the labor icon, etc. Keys are the
 * `summary.components` values the api emits; control-form widgets all share
 * the forms icon.
 */
const DAILY_REPORT_COMPONENT_ICONS: Record<string, string> = {
  bid_items: 'daily-report-page-menu-bid-item',
  material_deliveries: 'daily-report-page-menu-delivery',
  activities: 'daily-report-page-menu-activities',
  labor: 'daily-report-page-menu-labor',
  equipment: 'daily-report-page-menu-equipment',
  internal_tests: 'daily-report-page-menu-internal-test',
  external_tests: 'daily-report-page-menu-external-test',
  issues: 'daily-report-page-menu-issues',
  control_forms: 'daily-report-page-menu-forms',
};

const DAILY_REPORT_DEFAULT_ICON = 'daily-report-page-menu-activities';

/** Icon of the first matched component (api order); activities otherwise. */
export const dailyReportIconOf = (dailyReport: any): string => {
  const components = Array.isArray(dailyReport?.summary?.components) ? dailyReport.summary.components : [];
  const matched = components.find(component => DAILY_REPORT_COMPONENT_ICONS[component]);

  return matched ? DAILY_REPORT_COMPONENT_ICONS[matched] : DAILY_REPORT_DEFAULT_ICON;
};

/** Resolved icon for a result: per-result hook first, then the entity icon. */
export const iconOf = (config: SearchEntityConfig, result: any): string =>
  config?.iconOf?.(result) || config?.icon;

export const componentsList = (dailyReport: any): string => {
  const details = dailyReport?.summary?.component_details?.control_forms;
  const formWidgets = Array.isArray(details)
    ? [...new Set(details
      .map(item => FORM_WIDGET_COMPONENT_NAMES[item?.display_widget])
      .filter(Boolean))]
    : [];

  return dailyReport?.summary?.components
    ?.flatMap(component => (component === 'control_forms' && formWidgets.length
      ? formWidgets
      : [component.replace(/_/g, ' ')]))
    .join(', ');
};

/**
 * Shared builder for the daily-report deep link (matched/matchedId summary
 * params). Previously duplicated in header.component.ts and
 * daily-report-card.component.ts.
 */
export const buildDailyReportSearchNavigation = (dailyReport: any): SearchResultNavigation => {
  const tempMatched = [];
  const tempMatchedId = [];

  dailyReport?.summary?.components?.forEach(c => {
    tempMatched.push(`${componentNameMap?.[c]}_${dailyReport?.summary?.component_details?.[c]?.length}`);
  });

  Object.values(dailyReport?.summary?.component_details || {}).forEach((detail: any) => {
    detail.forEach(d => {
      tempMatchedId.push(`${dailyReport?.id}_${d?.id}_${d?.count}`);
    });
  });

  return {
    commands: [`projects/${dailyReport?.project?.id}/sites/${dailyReport?.site?.id}`],
    queryParams: {
      tab: 'daily-report',
      date: `${dailyReport?.report_date}`,
      reportId: `${dailyReport?.id}`,
      matched: tempMatched,
      matchedId: tempMatchedId,
    },
  };
};

export const SEARCH_ENTITIES: SearchEntityConfig[] = [
  {
    apiKey: 'dailyReports',
    searchType: 'dailyReport',
    label: 'Daily report',
    singularLabel: 'Daily report',
    tabSlug: 'daily-report',
    icon: DAILY_REPORT_DEFAULT_ICON,
    iconOf: dailyReportIconOf,
    createdAtField: 'creation_at',
    dateField: 'report_date',
    alwaysShowTab: true,
    card: 'dailyReport',
    titleOf: r => moment(r?.report_date, 'YYYY-MM-DD').isValid()
      ? moment(r?.report_date, 'YYYY-MM-DD').format('MMMM D, YYYY')
      : r?.report_date,
    metaOf: r => joinMeta([r?.project?.name, r?.site?.name]),
    matchInfoOf: r => {
      const components = componentsList(r);
      return `${r?.summary?.matches} matches${components ? ` (${components})` : ''}`;
    },
    navigationFor: buildDailyReportSearchNavigation,
  },
  {
    apiKey: 'issues',
    searchType: 'issues',
    label: 'Issues',
    singularLabel: 'Issue',
    tabSlug: 'issues',
    icon: 'daily-report-page-menu-issues',
    createdAtField: 'created_at',
    dateField: 'createdAt',
    alwaysShowTab: true,
    card: 'issue',
    titleOf: r => r?.title,
    // snippetOf mirrors what issue-card actually renders (the description) —
    // without it, matchedHintHtmlOf checks only the title and appends a
    // redundant "Matched:" hint under a description that is already
    // highlighted on the card.
    snippetOf: r => r?.description,
    metaOf: r => joinMeta([r?.project?.name, r?.site?.name]),
    // Project Issues tab, which loads the project details / issue types the
    // details form depends on (opening the panel directly on /search crashed
    // on the empty projectDetails store). `selectedId` is read on load by the
    // tab, which auto-opens the issue's details panel; `status=all` clears the
    // tab's default open filter so a closed issue's row is listed too.
    navigationFor: r => r?.project?.id ? {
      commands: [`projects/${r?.project?.id}`],
      queryParams: {
        tab: 'issues',
        status: 'all',
        ...(r?.id ? { selectedId: r?.id } : {}),
      },
    } : null,
  },
  {
    apiKey: 'dailyDiaries',
    searchType: 'dailyDiary',
    label: 'Daily diary',
    singularLabel: 'Daily diary',
    tabSlug: 'daily-diary',
    icon: 'daily-report-page-menu-forms',
    createdAtField: 'created_at',
    dateField: 'diary_date',
    card: 'generic',
    titleOf: r => moment(r?.diary_date, 'YYYY-MM-DD').isValid()
      ? `Diary — ${moment(r?.diary_date, 'YYYY-MM-DD').format('MMMM D, YYYY')}`
      : `Diary — ${r?.diary_date}`,
    snippetOf: r => (r?.remarks || [])
      .map(remark => remark?.description || remark?.title)
      .filter(Boolean)
      .join(' · '),
    metaOf: r => joinMeta([r?.project?.name, r?.site?.name, fullName(r?.createdBy)]),
    statusOf: r => r?.status,
    // No diary UI on web: land on the site's daily-report tab for the diary date.
    navigationFor: r => (r?.project?.id && r?.site?.id) ? {
      commands: [`projects/${r?.project?.id}/sites/${r?.site?.id}`],
      queryParams: { tab: 'daily-report', date: `${r?.diary_date}` },
    } : null,
  },
  {
    apiKey: 'rfis',
    searchType: 'rfi',
    label: 'RFIs',
    singularLabel: 'RFI',
    tabSlug: 'rfis',
    icon: 'daily-report-page-menu-forms',
    createdAtField: 'created_at',
    dateField: 'createdAt',
    card: 'generic',
    titleOf: r => [r?.rfi_no ? `#${r?.rfi_no}` : '', r?.title].filter(Boolean).join(' '),
    snippetOf: r => r?.description,
    metaOf: r => [r?.project?.name, fullName(r?.createdBy)].filter(Boolean).join(' . '),
    statusOf: r => r?.status,
    // Project RFIs tab (log). `rifId` (existing param name, misspelling and
    // all) is read on load by rfis-list, which auto-opens the details panel
    // the same way a row click would.
    navigationFor: r => r?.project?.id ? {
      commands: [`projects/${r?.project?.id}`],
      queryParams: {
        tab: 'rfis',
        ...(r?.id ? { rifId: r?.id } : {}),
      },
    } : null,
  },
  {
    apiKey: 'submittals',
    searchType: 'submittal',
    label: 'Submittals',
    singularLabel: 'Submittal',
    tabSlug: 'submittals',
    // 'add-file' renders as a "+" — reads as an add action, not a result.
    icon: 'paperclip',
    createdAtField: 'created_at',
    dateField: 'createdAt',
    card: 'generic',
    titleOf: r => [r?.submittal_no ? `#${r?.submittal_no}` : '', r?.title].filter(Boolean).join(' '),
    snippetOf: r => r?.description,
    metaOf: r => [
      r?.project?.name,
      r?.spec_reference_no ? `Spec ${r?.spec_reference_no}` : '',
      fullName(r?.createdBy),
    ].filter(Boolean).join(' . '),
    statusOf: r => r?.status,
    // Project Submittals tab; subTab=list is the Log sub-tab (the default).
    // `submittalsItemId` (the existing row-click param) is read on load by
    // submittals-list, which auto-opens the details panel.
    navigationFor: r => r?.project?.id ? {
      commands: [`projects/${r?.project?.id}`],
      queryParams: {
        tab: 'submittals',
        subTab: 'list',
        ...(r?.id ? { submittalsItemId: r?.id } : {}),
      },
    } : null,
  },
  {
    apiKey: 'documents',
    searchType: 'document',
    label: 'Documents',
    singularLabel: 'Document',
    tabSlug: 'documents',
    icon: 'folder',
    createdAtField: 'created_at',
    dateField: 'createdAt',
    card: 'generic',
    titleOf: r => r?.name,
    snippetOf: r => r?.description
      || (r?.attachments || []).map(a => a?.original_name).filter(Boolean).join(' · '),
    metaOf: r => [
      r?.folder?.name,
      r?.project?.name || r?.organization?.name,
      fullName(r?.createdBy),
    ].filter(Boolean).join(' . '),
    // Project Documents tab. `documentId` is read on load by documents-table,
    // which walks the in-memory breadcrumb to the document's folder and opens
    // the same carousel a row click would. Org-level documents (project null)
    // have no web page at all — those results render but are not navigable.
    navigationFor: r => r?.project?.id ? {
      commands: [`projects/${r?.project?.id}`],
      queryParams: {
        tab: 'documents',
        ...(r?.id ? { documentId: r?.id } : {}),
      },
    } : null,
  },
  {
    apiKey: 'materialTests',
    searchType: 'materialTest',
    label: 'Material tests',
    singularLabel: 'Material test',
    tabSlug: 'material-tests',
    icon: 'daily-report-page-menu-external-test',
    createdAtField: 'created_at',
    dateField: 'report_date',
    card: 'generic',
    titleOf: r => r?.material_test?.name,
    snippetOf: r => r?.comment
      || r?.inspection_detail?.comment
      || (r?.lab_recommendation
        ? `Lab recommendation: ${MATERIAL_TEST_STATUS_LABELS[r?.lab_recommendation] || r?.lab_recommendation}`
        : ''),
    metaOf: r => [
      r?.project?.name,
      r?.lab?.name,
      r?.bid_item?.item,
    ].filter(Boolean).join(' . '),
    statusOf: r => MATERIAL_TEST_STATUS_LABELS[r?.status] || r?.status,
    // Global offsite-tests page (project-scoped via the route param).
    // `testId` is read on load and auto-opens the test's detail panel the
    // same way clicking its card would.
    navigationFor: r => r?.project?.id ? {
      commands: [`/offsite-tests/project/${r?.project?.id}`],
      ...(r?.id ? { queryParams: { testId: r?.id } } : {}),
    } : null,
  },
  {
    apiKey: 'punchlists',
    searchType: 'punchlist',
    label: 'Punch lists',
    singularLabel: 'Punch list',
    tabSlug: 'punchlists',
    icon: 'daily-report-page-menu-issues',
    createdAtField: 'created_at',
    dateField: 'createdAt',
    card: 'generic',
    titleOf: r => r?.entity_type === 'punchlist_item'
      ? [r?.item_number ? `#${r?.item_number}` : '', r?.title].filter(Boolean).join(' ')
      : r?.name,
    snippetOf: r => r?.description,
    metaOf: r => r?.entity_type === 'punchlist_item'
      ? [r?.punchlist?.name, r?.project?.name, r?.severity].filter(Boolean).join(' . ')
      : [r?.project?.name].filter(Boolean).join(' . '),
    statusOf: r => r?.status,
    // Project Punchlist tab items view. punchlistId + subTab=items are read
    // by punchlist-wrapper; `itemId` (punchlist_item results only) is read by
    // punchlist-items-list, which auto-opens the item's details panel.
    navigationFor: r => {
      if (!r?.project?.id) {
        return null;
      }
      const isItem = r?.entity_type === 'punchlist_item';
      const punchlistId = isItem ? r?.punchlist?.id : r?.id;
      return {
        commands: [`projects/${r?.project?.id}`],
        queryParams: {
          tab: 'punchlist',
          subTab: 'items',
          ...(punchlistId ? { punchlistId } : {}),
          ...(isItem && r?.id ? { itemId: r?.id } : {}),
        },
      };
    },
  },
  {
    apiKey: 'warrantyClaims',
    searchType: 'warrantyClaim',
    label: 'Warranty claims',
    singularLabel: 'Warranty claim',
    tabSlug: 'warranty-claims',
    icon: 'daily-report-page-menu-forms',
    createdAtField: 'created_at',
    dateField: 'createdAt',
    card: 'generic',
    titleOf: r => [r?.claim_number, r?.title].filter(Boolean).join(' '),
    snippetOf: r => r?.description || r?.proposed_solution,
    metaOf: r => [
      r?.project?.name,
      r?.warranty?.name,
      r?.severity,
    ].filter(Boolean).join(' . '),
    statusOf: r => r?.status,
    // Project Warranty tab, Claims sub-tab (tab read by the project
    // tab-group, subTab by warranty-wrapper). `claimId` is read on load by
    // warranty-claims-list, which auto-opens the claim's right panel.
    navigationFor: r => r?.project?.id ? {
      commands: [`projects/${r?.project?.id}`],
      queryParams: {
        tab: 'warranty',
        subTab: 'claims',
        ...(r?.id ? { claimId: r?.id } : {}),
      },
    } : null,
  },
  {
    apiKey: 'samples',
    searchType: 'sample',
    label: 'Samples',
    singularLabel: 'Sample',
    tabSlug: 'samples',
    icon: 'daily-report-page-menu-internal-test',
    createdAtField: 'created_at',
    dateField: 'sample_date',
    card: 'generic',
    titleOf: r => r?.name,
    snippetOf: r => r?.comment
      || r?.source_value
      || r?.mix_design_value
      || (r?.tests || []).map(t => t?.material_test_value).filter(Boolean).join(' · '),
    metaOf: r => [
      r?.project?.name,
      r?.material?.name,
      r?.sample_type,
    ].filter(Boolean).join(' . '),
    // No top-level status on samples (tests carry their own numeric statuses),
    // so no status pill.
    // Project Samples tab (org-gated on sample_record_general config).
    // `sampleId` (the existing row-click param) is read on load by
    // sample-records-list, which auto-opens the sample's details panel.
    navigationFor: r => r?.project?.id ? {
      commands: [`projects/${r?.project?.id}`],
      queryParams: {
        tab: 'samples',
        ...(r?.id ? { sampleId: r?.id } : {}),
      },
    } : null,
  },
];

export const searchEntityBySearchType: Record<string, SearchEntityConfig> =
  SEARCH_ENTITIES.reduce((acc, entity) => ({ ...acc, [entity.searchType]: entity }), {});

/** searchType of the cross-category tab — not a real entity in the payload. */
export const ALL_RESULTS_SEARCH_TYPE = 'all';

/**
 * Pseudo-entry for the leading "All" tab. It carries no apiKey or card: the
 * results list special-cases it, merging every type's results and rendering
 * each row with its own registry entry (so cards and clicks behave exactly
 * as they do inside the category tabs).
 */
export const ALL_RESULTS_TAB: SearchEntityConfig = {
  apiKey: '',
  searchType: ALL_RESULTS_SEARCH_TYPE,
  label: 'All',
  singularLabel: 'All',
  tabSlug: ALL_RESULTS_SEARCH_TYPE,
  icon: '',
  createdAtField: 'createdAt',
  dateField: '',
  alwaysShowTab: true,
  card: 'generic',
};
